import { Observable, of, throwError, timer } from "rxjs";
import { catchError, map, switchMapTo, take } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { ExifWrapperResult } from "../../_objects/inspection-image";
import { MarkItem } from "../../_objects/mark";
import { TitleItem } from "../../_objects/title";
import { FileSystemAccessService } from "../../_services/file-system-access.service";
import { IPC_CHANNEL, MEASUREMENT } from "../electron.constants";
import { InspectionImageSchema } from "../models/inspectionImage.schema";
import { MarkSchema } from "../models/mark.schema";
import { WkHtmlToPdfOptions } from "../models/report-preview/wkHtml-to-pdf-options";
import { TitleSchema } from "../models/title.schema";
import { IMAGE_FILETYPE, MARK_DISABLED_COLOR } from "../../app.constants";
import { ElectronService } from "../../electron.service";
import { VisolPrintToPDFOptions } from "../../app-types";

@Injectable({
  providedIn: "root"
})
export class OfflineUtilsService {
  readonly imageExtensions = [
    IMAGE_FILETYPE.EXTENSION_JPEG,
    IMAGE_FILETYPE.EXTENSION_JPG,
    IMAGE_FILETYPE.EXTENSION_PNG,
    IMAGE_FILETYPE.EXTENSION_BMP,
    IMAGE_FILETYPE.EXTENSION_HEIC,
    IMAGE_FILETYPE.EXTENSION_HEIF,
    IMAGE_FILETYPE.EXTENSION_TIF,
    IMAGE_FILETYPE.EXTENSION_TIFF
  ];
  readonly imageMimeTypes = [
    IMAGE_FILETYPE.JPEG,
    IMAGE_FILETYPE.JPG,
    IMAGE_FILETYPE.PNG,
    IMAGE_FILETYPE.BMP,
    IMAGE_FILETYPE.HEIC,
    IMAGE_FILETYPE.HEIF,
    IMAGE_FILETYPE.TIF,
    IMAGE_FILETYPE.TIFF
  ];

  constructor(
    private electronService: ElectronService,
    private fileSystemAccessService: FileSystemAccessService
  ) {}

  /** get file object from local file storage */
  getImageObj(image: InspectionImageSchema) {
    const separator = this.fileSystemAccessService.getSystemPathSeparator();
    const filePath = image.localFilePath.replace(/\//g, separator);
    return this.fileSystemAccessService.getFile(filePath, image.fileName, image.mimeType);
  }

  /** get modified file object from local file storage */
  getModifiedImageObj(image: InspectionImageSchema): Promise<File | null> {
    const separator = this.fileSystemAccessService.getSystemPathSeparator();
    if (!image.modifiedItems) {
      return Promise.resolve(null);
    }
    const filePathParts = image.localFilePath.split(/\\|\//);
    const _fileName = "modified_" + filePathParts[filePathParts.length - 1];
    filePathParts.splice(filePathParts.length - 1, 0, "modified");
    filePathParts[filePathParts.length - 1] = _fileName;
    const modifiedPath = filePathParts.join(separator);
    return this.fileSystemAccessService.getFile(modifiedPath, image.fileName, image.mimeType);
  }

  /**
   * get measurement file object from local file storage
   *
   * @param image InspectionImageSchema。
   * @returns Measurement File instance
   */
  public async getMeasurementImageObj(image: InspectionImageSchema): Promise<File | null> {
    if (!image.measurementItems) {
      return Promise.resolve(null);
    }
    const separator = this.fileSystemAccessService.getSystemPathSeparator();
    const filePathParts: Array<string> = image.localFilePath.split(/\\|\//);
    const measurementFileName: string = MEASUREMENT.FILE_PREFIX + filePathParts.pop();
    filePathParts.push(MEASUREMENT.DIRECTORY_NAME);
    filePathParts.push(measurementFileName);
    const measurementPath: string = filePathParts.join(separator);
    return this.fileSystemAccessService.getFile(measurementPath, image.fileName, image.mimeType);
  }

  /** get thumbnail file object from local file storage */
  async getThumbnailImageObj(image: InspectionImageSchema) {
    const separator = this.fileSystemAccessService.getSystemPathSeparator();
    const thumbFilePath = image.thumbFilePath.replace(/\//g, separator);

    if (image.mimeType === IMAGE_FILETYPE.MP4) {
      let file;
      for (const ext of this.imageExtensions) {
        const fileNameParts = image.fileName.split(".");
        const namePart = fileNameParts.slice(0, -1).join(".");
        const fileName = `${namePart}.${ext}`;
        const filePath = thumbFilePath.replace(image.fileName, fileName);
        file = await this.fileSystemAccessService.getFile(filePath, fileName, this.getImageMimeType(ext));
        if (file && file.size > 0) {
          break;
        }
      }
      return file;
    }

    return this.fileSystemAccessService.getFile(thumbFilePath, image.fileName, image.mimeType);
  }

  getImageMimeType(extension: string): string {
    return this.imageMimeTypes.find((mimeType) => mimeType.includes(extension));
  }

  /** get modified thumbnail file object from local file storage */
  getModifiedThumbnailImageObj(image: InspectionImageSchema): Promise<File | null> {
    const separator = this.fileSystemAccessService.getSystemPathSeparator();
    if (!image.modifiedItems) {
      return Promise.resolve(null);
    }
    const filePathParts = image.thumbFilePath.split("/");
    const _fileName = "modified_" + filePathParts[filePathParts.length - 1];
    filePathParts.splice(filePathParts.length - 1, 0, "modified");
    filePathParts[filePathParts.length - 1] = _fileName;
    const modifiedPath = filePathParts.join("/").replace(/\//g, separator);
    return this.fileSystemAccessService.getFile(modifiedPath, image.fileName, image.mimeType);
  }

  /** get measurement thumbnail file object from local file storage */
  public async getMeasurementThumbnailImageObj(image: InspectionImageSchema): Promise<File | null> {
    const separator = this.fileSystemAccessService.getSystemPathSeparator();
    if (!image.measurementItems) {
      return Promise.resolve(null);
    }
    const filePathParts: Array<string> = image.localFilePath.split(/\\|\//);
    const measurementFileName: string = MEASUREMENT.FILE_PREFIX + filePathParts.pop();
    filePathParts.push(MEASUREMENT.DIRECTORY_NAME);
    filePathParts.push(measurementFileName);
    const measurementPath = filePathParts.join("/").replace(/\//g, separator);
    return this.fileSystemAccessService.getFile(measurementPath, image.fileName, image.mimeType);
  }

  loadAppUrl() {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.LOAD_APP, {}).toPromise();
  }

  getAppDirectory() {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.GET_APP_DIRECTORY, {});
  }

  printToPdf(targetPath: string, options: VisolPrintToPDFOptions) {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.PRINT_TO_PDF, targetPath, options);
  }

  // https://ossa-cloud.visualstudio.com/OSD%20Visol%20Solution/_sprints/backlog/OSD%20ViSOL%20for%20Brian%20Team/OSD%20Visol%20Solution/OSD%20ViSOL%20for%20Brian%20Iterations/FY24%20Second%20Quarter?workitem=29571
  startAppExe(isBackground: boolean, token: string) {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.IPLEX_ONE, isBackground, token);
  }
  endExe() {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.IPLEX_ONE_END);
  }

  /**
   * Convert the image to JPEG.
   *
   * @param imageFilePath Path of the image file.
   * @param convertedImageFilePath Path of the converted image file.
   * @returns Returns the success or failure of the execution.
   */
  public convertToJpeg(imageFilePath: string, convertedImageFilePath: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.CONVERT_TO_JPEG, imageFilePath, convertedImageFilePath);
  }

  /**
   * Convert the image to MP4.
   *
   * @param multimediaInputFilePath Path of the input audio-video file.
   * @param multimediaOutputFilePath Path of the converted audio-video file.
   * @returns Returns the success or failure of the execution.
   */
  public processAviConversionToMp4(multimediaInputFilePath: string, multimediaOutputFilePath: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.CONVERT_TO_MP4, multimediaInputFilePath, multimediaOutputFilePath);
  }

  /**
   * Exit the application.
   */
  public exitApp(): Observable<any> {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.EXIT_APP);
  }

  /**
   * Read the EXIF information of the image.
   *
   * @param imageFilePath Path of the image file.
   * @param outputJsonFilePath Path for the output JSON file.
   * @param isDataPlate Whether it is a DataPlate.
   * @param mimeType MIME type.
   * @param outputThumbnailFilePath Path for the output thumbnail.
   * @returns Returns the EXIF information.
   */
  public readExifInfo(
    imageFilePath: string,
    outputJsonFilePath: string,
    isDataPlate: boolean,
    mimeType: string,
    outputThumbnailFilePath: string
  ): Observable<ExifWrapperResult> {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.READ_EXIF_INFO, imageFilePath, outputJsonFilePath, isDataPlate, mimeType, outputThumbnailFilePath).pipe(
      map((x) => x as ExifWrapperResult)
    );
  }

  public getLocalFolderPath(): Observable<string[]> {
    return this.ipcSendSync(IPC_CHANNEL.UTILS.GET_LOCAL_FOLDER_PATH);
  }

  protected ipcSendSync(channel: string, ...args: any[]) {
    const sendSyncOf = of(this.electronService.sendSyncCommand(channel, ...args));
    return timer(50)
      .pipe(switchMapTo(sendSyncOf))
      .pipe(take(1))
      .pipe(catchError((error: any) => throwError(() => error.json)));
  }

  generateMarksText(markItems: MarkItem[]) {
    // Only marks A, B, C, D, X work with devices
    const result = markItems
      .filter((item) => item.identifier < 5)
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((item) => {
        if (item.color === MARK_DISABLED_COLOR) {
          item.label = "";
        }
        return `${item.code}:${item.label}`;
      })
      .join("\r\n");

    return result + "\r\n";
  }

  generateTitlesText(titleSchema: TitleSchema) {
    if (!titleSchema.items) {
      return "";
    }

    const items = JSON.parse(titleSchema.items) as TitleItem[];

    // Limit output to the first 50 items and append \r\n at the end
    const result = items
      .filter((item, i) => i < 50)
      .map((item) => item.identifier)
      .join("\r\n");

    return result + "\r\n";
  }
}
